import { useCallback, useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { apiGetJson, apiSendJson, clientRequestHeaders } from "@/lib/sm-api";

type PlatformFlags = { onboarding_online_enabled: boolean; pwa_enabled: boolean };

const PLATFORM_FROM = "market@structura.com.ar";

/** Tab «Correo y plataforma» — solo Resend + flags globales (paridad Booking Hub). */
export function AdminPlatformEmailPanel({
  userEmail,
}: {
  userEmail?: string;
  tenants: { id: string; name: string }[];
}) {
  const [resendKey, setResendKey] = useState("");
  const [resendConfigured, setResendConfigured] = useState(false);
  const [emailTestTo, setEmailTestTo] = useState("");
  const [emailTestReplyTo, setEmailTestReplyTo] = useState("");
  const [emailTestLoading, setEmailTestLoading] = useState(false);
  const [emailSaveLoading, setEmailSaveLoading] = useState(false);
  const [flags, setFlags] = useState<PlatformFlags | null>(null);
  const [flagSaving, setFlagSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [emailRes, flagsRes] = await Promise.all([
        apiGetJson<{ resendApiKeySet: boolean }>("/api/admin/platform-email"),
        apiGetJson<{ flags: PlatformFlags }>("/api/admin/platform-flags"),
      ]);
      setResendConfigured(emailRes.resendApiKeySet);
      setFlags(flagsRes.flags);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleFlag = async (key: keyof PlatformFlags, value: boolean) => {
    setFlagSaving(true);
    try {
      await apiSendJson("/api/admin/platform-flags", "PATCH", { key, value });
      setFlags((f) => (f ? { ...f, [key]: value } : f));
      toast.success("Flag actualizado");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al actualizar flag");
      void load();
    } finally {
      setFlagSaving(false);
    }
  };

  const saveResendKey = async () => {
    setEmailSaveLoading(true);
    try {
      const r = await fetch("/api/admin/platform-email", {
        method: "PATCH",
        credentials: "include",
        headers: { ...clientRequestHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ resendApiKey: resendKey.trim() || null }),
      });
      const d = (await r.json()) as { error?: string; resendApiKeySet?: boolean };
      if (!r.ok) {
        toast.error(d.error ?? "No se pudo guardar");
        return;
      }
      setResendConfigured(Boolean(d.resendApiKeySet));
      setResendKey("");
      toast.success("API key guardada");
    } catch {
      toast.error("Error de red");
    } finally {
      setEmailSaveLoading(false);
    }
  };

  const runEmailTest = async () => {
    const to = emailTestTo.trim() || userEmail;
    if (!to) {
      toast.error("Indicá un email de prueba");
      return;
    }
    setEmailTestLoading(true);
    try {
      await apiSendJson("/api/admin/email-test", "POST", {
        to,
        replyTo: emailTestReplyTo.trim() || undefined,
        resendApiKey: resendKey.trim() || undefined,
      });
      toast.success("Correo de prueba enviado");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error en prueba");
    } finally {
      setEmailTestLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-xs text-muted-foreground">
        Correo transaccional centralizado (Resend) y flags globales. Infraestructura, pagos, billing y catálogo de
        features están en <strong>Panel → Estado de plataforma</strong>. Eventos por workspace en{" "}
        <Link to="/admin/ops/events" search={{} as never} className="text-primary underline-offset-2 hover:underline">
          Operaciones → Eventos
        </Link>
        .
      </p>

      <div className="glass-card space-y-4 rounded-xl p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium">Resend — plataforma</p>
          <Badge variant={resendConfigured ? "default" : "secondary"}>
            {resendConfigured ? "API key configurada" : "Sin API key"}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Todos los avisos salen desde <code className="rounded bg-secondary px-1">{PLATFORM_FROM}</code>. El{" "}
          <strong>Reply-To</strong> de cada mensaje es el email del workspace cuando aplica.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-muted-foreground">API key Resend (global)</Label>
            <Input
              type="password"
              autoComplete="off"
              className="h-9 bg-secondary/50 font-mono text-sm"
              placeholder={resendConfigured ? "••••••••  (vacío = no cambiar)" : "re_…"}
              value={resendKey}
              onChange={(e) => setResendKey(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">From (fijo)</Label>
            <Input className="h-9 bg-secondary/30 text-sm" value={PLATFORM_FROM} readOnly disabled />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Reply-To de prueba</Label>
            <Input
              className="h-9 bg-secondary/50 text-sm"
              type="email"
              placeholder="email@del-workspace.com"
              value={emailTestReplyTo}
              onChange={(e) => setEmailTestReplyTo(e.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-muted-foreground">Destino prueba</Label>
            <Input
              className="h-9 bg-secondary/50 text-sm"
              type="email"
              placeholder={userEmail ?? "email@ejemplo.com"}
              value={emailTestTo}
              onChange={(e) => setEmailTestTo(e.target.value)}
            />
          </div>
        </div>

        {flags && (
          <>
            <div className="flex items-center justify-between rounded-lg border border-border/70 bg-secondary/20 px-3 py-2">
              <div>
                <p className="text-sm font-medium">Onboarding 100% online</p>
                <p className="text-xs text-muted-foreground">
                  Si se desactiva, los owners no verán el flujo online en `/app/onboarding`.
                </p>
              </div>
              <Switch
                checked={flags.onboarding_online_enabled}
                disabled={flagSaving}
                onCheckedChange={(v) => void toggleFlag("onboarding_online_enabled", v)}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border/70 bg-secondary/20 px-3 py-2">
              <div>
                <p className="text-sm font-medium">PWA — kill-switch global</p>
                <p className="text-xs text-muted-foreground">
                  Apaga el service worker en toda la plataforma. El entitlement por tenant es la feature
                  pwa_install (Feature flags / Planes).
                </p>
              </div>
              <Switch
                checked={flags.pwa_enabled}
                disabled={flagSaving}
                onCheckedChange={(v) => void toggleFlag("pwa_enabled", v)}
              />
            </div>
          </>
        )}

        <div className="flex flex-wrap gap-2 border-t border-border pt-4">
          <Button type="button" size="sm" disabled={emailSaveLoading} onClick={() => void saveResendKey()}>
            {emailSaveLoading ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Guardando…
              </>
            ) : (
              "Guardar API key"
            )}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={emailTestLoading}
            onClick={() => void runEmailTest()}
          >
            {emailTestLoading ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Enviando…
              </>
            ) : (
              "Enviar prueba"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
